import qs from 'querystring';

import { fetchJSON } from '@bonnet/next/fetch';

import { getPath, pageNames } from '@atc/bonnet-paths';
import { cmApiUrl } from '@atc/cm-api-url';

import { sendImpressions } from 'reaxl-analytics';

import { inventoryImpressions } from '@/analytics/srpAnalyticsHandlers';

import inventoryDuck from '@/ducks/inventoryDuck.js';
import ownersDuck from '@/ducks/ownersDuck.js';
import srpPaginationDuck from '@/ducks/srp/srpPaginationDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpSpotlightDuck from '@/ducks/srp/srpSpotlightDuck.js';

import { getFourthSpotlightTier, listingDataPreprocessing } from './dispatchUtils';

const { VEHICLE_DETAILS } = pageNames;

/**
 * Fetches listing data from multiple endpoints in parallel
 */
const fetchListingsData = async (searchQuery, spotlightPage, shouldFetchSpotlights) => {
    const listingsEndpoint = cmApiUrl('/rest/lsc/listing');
    const premiumSpotlightEndpoint = cmApiUrl('/rest/lsc/listing/spotlight/premiumresults');
    const standardSpotlightEndpoint = cmApiUrl('/rest/lsc/listing/spotlight/results');

    const emptyResponse = { listings: [], owners: [] };

    return Promise.all([
    // Regular listings
        fetchJSON(`${listingsEndpoint}?${qs.stringify(searchQuery)}`),

        // Premium spotlights
        shouldFetchSpotlights
            ? fetchJSON(`${premiumSpotlightEndpoint}?${qs.stringify({
                ...searchQuery,
                numSpotlights: 3,
                listingTier: 'premium',
                spotlightPage,
            })}`)
            : Promise.resolve(emptyResponse),

        // Featured spotlights
        shouldFetchSpotlights
            ? fetchJSON(`${standardSpotlightEndpoint}?${qs.stringify({
                ...searchQuery,
                numSpotlights: 6,
                listingTier: 'featured',
                spotlightPage,
            })}`)
            : Promise.resolve(emptyResponse),

        // Standard spotlights
        shouldFetchSpotlights
            ? fetchJSON(`${standardSpotlightEndpoint}?${qs.stringify({
                ...searchQuery,
                numSpotlights: 6,
                listingTier: 'standard',
                spotlightPage,
            })}`)
            : Promise.resolve(emptyResponse),
    ]);
};

/**
 * Combines spotlight listings based on tier priority
 * - Always includes all premium spotlights (0-3)
 * - Adds exactly one spotlight from the fourth tier in the 4th position
 * - Ensures no duplicates between tiers
 */
const combineSpotlights = (premiumSpotlights, featuredSpotlights, standardSpotlights, fourthTierSpotlight) => {
    const result = []; // Start with up to 3 premium spotlights

    // If we have a fourth tier spotlight and premium spotlights are less than 4
    if (fourthTierSpotlight && (fourthTierSpotlight === 'Standard' || fourthTierSpotlight === 'Featured')) {
        // Determine which tier's spotlights to use
        const tierSpotlights = fourthTierSpotlight === 'Standard' ? standardSpotlights : featuredSpotlights;

        if (tierSpotlights.length > 0) {
            // Create a Set of premium spotlight IDs for faster lookup
            const premiumIds = new Set(premiumSpotlights.map((spotlight) => spotlight.id));

            // Find the first spotlight from the fourth tier that isn't a duplicate
            const uniqueSpotlight = tierSpotlights.find((spotlight) => !premiumIds.has(spotlight.id));

            // Add the unique spotlight if found
            if (uniqueSpotlight) {
                result.push(uniqueSpotlight);
            }
        }
    }

    return result;
};

/**
 * Get unique owners by filtering duplicates
 */
const getUniqueOwners = (ownersList) => {
    const uniqueOwnerIds = new Set();
    return ownersList.filter((owner) => {
        if (!owner?.id) return false;
        const isDuplicate = uniqueOwnerIds.has(owner.id);
        uniqueOwnerIds.add(owner.id);
        return !isDuplicate;
    });
};

/**
 * Process listings with common preprocessing logic
 */
const processListings = (listings, owners, config) => {
    const { displayPhoneNumber, hideOwnerName, inventoryImageUrl, vdpUrl } = config;

    listings.forEach((listing) => {
        listingDataPreprocessing({
            listing,
            owners,
            displayPhoneNumber,
            hideOwnerName,
            inventoryImageUrl,
            vdpUrl,
        });
    });
};

/**
 * Update Redux store with new listings and spotlights
 */
const updateReduxStore = (dispatch, { activeListings, premiumSpotlights, fourthTierSpotlights, owners, uniqueSpotlightOwners, firstRecord }) => {

    // Add regular listings to store
    if (activeListings?.length) {
        dispatch(inventoryDuck.creators.addInventory(activeListings));
        dispatch(ownersDuck.creators.addOwners(owners));
        dispatch(srpResultsDuck.creators.addActiveResults(activeListings.map((listing) => listing.id), true));
    }

    dispatch(srpSpotlightDuck.creators.addActiveResults(premiumSpotlights.map((spotlight) => spotlight.id), true));
    dispatch(srpSpotlightDuck.creators.setSpotlightTypes({ premium: [...premiumSpotlights], fourthTier: fourthTierSpotlights }));

    if (premiumSpotlights?.length) {
        dispatch(inventoryDuck.creators.addInventory(premiumSpotlights));
    }

    // Add all spotlight listings to store
    if (fourthTierSpotlights?.length) {
        dispatch(inventoryDuck.creators.addInventory(fourthTierSpotlights));

        // Add to spotlight duck
        dispatch(srpSpotlightDuck.creators.addActiveResults(
            fourthTierSpotlights.filter(Boolean).map((item) => item.id),
            true // Append to existing spotlights
        ));
    }

    // Add unique spotlight owners
    if (uniqueSpotlightOwners.length) {
        dispatch(ownersDuck.creators.addOwners(uniqueSpotlightOwners));
    }

    // Update pagination
    if (activeListings?.length) {
        const currentPage = Math.floor(firstRecord / 30) + 1;
        const nextPage = currentPage + 1;

        dispatch(srpPaginationDuck.creators.setCurrentPage(nextPage));
    }
};

/**
 * Send analytics events for listings and spotlights
 */
const sendAnalyticsEvents = (activeListings, spotlights, fourthTierSpotlight, firstRecord, spotlightPage) => {
    // Analytics for regular listings
    if (activeListings?.length) {
        sendImpressions({
            name: inventoryImpressions,
            data: {
                cmp: 'lstg',
                startingIndex: firstRecord,
                selectedItemIds: activeListings.map((listing) => listing.id),
                pixallData: { rank: firstRecord },
            },
        });
    }

    // Analytics for spotlights by tier
    const { premiumSpotlights, fourthTierSpotlights } = spotlights;

    const spotlightTiers = [
        { listings: premiumSpotlights, tier: 'premium' },
        { listings: fourthTierSpotlights, tier: fourthTierSpotlight },
    ];

    spotlightTiers.forEach(({ listings, tier }) => {
        if (listings?.length) {
            sendImpressions({
                name: inventoryImpressions,
                data: {
                    cmp: 'spotlight',
                    selectedItemIds: listings.map((spotlight) => spotlight.id),
                    pixallData: { rank: `spotlight-${tier}-page-${spotlightPage}` },
                },
            });
        }
    });
};

/**
 * Fetches more listings and spotlights for infinite scroll functionality
 */
export const fetchMoreListings = async ({
    listings,
    query,
    dispatch,
    router,
    featureFlags,
    inventoryImageUrl,
    sort,
    brand,
    channel,
    vhrProviders,
    relevanceConfig,
    onComplete,
    zip,
}) => {
    const {
        enableExperienceQueryParams,
        queryParams,
        enableSpotlightNonRelevantSort,
        hideSpotlights,
        displayPhoneNumber,
        hideOwnerName,
    } = featureFlags;

    const firstRecord = listings.length;
    const searchQuery = {
        ...query,
        channel,
        vhrProviders,
        relevanceConfig,
        firstRecord,
    };

    if (enableExperienceQueryParams) {
        Object.assign(searchQuery, { ...queryParams });
    }

    try {
        const spotlightPage = Math.floor(firstRecord / 30) + 1;
        const isRelevantSort = sort === 'relevance';
        const shouldFetchSpotlights = !enableSpotlightNonRelevantSort
      || ((hideSpotlights || enableSpotlightNonRelevantSort) && isRelevantSort);

        const [
            listingsResponse,
            premiumSpotlightsResponse,
            featuredSpotlightsResponse,
            standardSpotlightsResponse,
        ] = await fetchListingsData(searchQuery, spotlightPage, shouldFetchSpotlights);

        const { listings: activeListings = [], owners = [] } = listingsResponse;
        const { listings: premiumSpotlights = [], owners: premiumOwners = [] } = premiumSpotlightsResponse;
        const { listings: featuredSpotlights = [] } = featuredSpotlightsResponse;
        const { listings: standardSpotlights = [] } = standardSpotlightsResponse;

        const fourthTierSpotlight = getFourthSpotlightTier(
            activeListings,
        );

        // Combine spotlights and deduplicate owners
        const fourthTierSpotlights = combineSpotlights(
            premiumSpotlights,
            featuredSpotlights,
            standardSpotlights,
            fourthTierSpotlight
        );

        const spotlightOwners = [...premiumOwners];
        const uniqueSpotlightOwners = getUniqueOwners(spotlightOwners);

        // Get VDP URL for deep linking
        const vdpUrl = await getPath(VEHICLE_DETAILS, {}, {
            useUpdatedVdpPath: true,
        });

        const processingConfig = { displayPhoneNumber, hideOwnerName, inventoryImageUrl, vdpUrl };

        processListings(activeListings, owners, processingConfig);
        processListings(premiumSpotlights, owners, processingConfig);

        if (fourthTierSpotlights.length > 0) {
            processListings(fourthTierSpotlights, uniqueSpotlightOwners, processingConfig);
        }

        updateReduxStore(dispatch, {
            activeListings,
            premiumSpotlights,
            fourthTierSpotlights,
            owners,
            uniqueSpotlightOwners,
            firstRecord,
        });

        sendAnalyticsEvents(
            activeListings,
            { premiumSpotlights, fourthTierSpotlights },
            fourthTierSpotlight,
            firstRecord,
            spotlightPage
        );

        if (onComplete && typeof onComplete === 'function') {
            onComplete();
        }

        return { activeListings, fourthTierSpotlights };
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching more data:', error);
        return { activeListings: [], fourthTierSpotlights: [] };
    }
};

export default fetchMoreListings;
